import { useRef, useState, type ReactNode } from 'react'
import { Loader2, Upload } from 'lucide-react'

import { cn } from '@/lib/utils'

interface FileDropzoneProps {
  /** `accept` of the file input, e.g. ".html,text/html". */
  accept?: string
  onFile: (file: File) => void
  /** Shown while nothing has been picked. */
  label: string
  /** What has been picked, replacing the label — e.g. the file name and a summary. */
  children?: ReactNode
  busy?: boolean
  disabled?: boolean
  className?: string
}

/** A click-or-drop target for a single file. */
export function FileDropzone({
  accept,
  onFile,
  label,
  children,
  busy = false,
  disabled = false,
  className,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const inactive = busy || disabled

  return (
    <>
      <button
        type="button"
        disabled={inactive}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          if (!inactive) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          const file = e.dataTransfer.files?.[0]
          if (file && !inactive) onFile(file)
        }}
        className={cn(
          'flex min-h-24 w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-(--ad-border) px-4 py-5 text-center text-sm transition-colors',
          'hover:border-(--ad-border-strong) focus-visible:border-(--ad-primary) focus-visible:outline-none disabled:cursor-default disabled:opacity-60',
          dragging && 'border-(--ad-primary) bg-(--ad-primary-subtle)',
          className
        )}
      >
        {busy ? (
          <Loader2 className="size-5 animate-spin text-(--ad-text-muted)" />
        ) : children ? (
          children
        ) : (
          <>
            <Upload className="size-5 text-(--ad-text-muted)" />
            <span className="text-(--ad-text-muted)">{label}</span>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onFile(file)
          e.target.value = ''
        }}
      />
    </>
  )
}
